import { TabButtonGroup } from 'src/Components/TabButton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Stack } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { MappedChartType } from '../../../system-data-handler/types'
import type { TabButtonOption } from 'src/Components/TabButton/TabButtonGroup'
import { DASHBOARD_BUILDING_CHART_BUTTON_ID, DASHBOARD_DIRECT_CONSUMPTION_CHART_BUTTON_ID } from 'src/Libs/qa/ids'
import { PresetChartType, PresetChartTypes, usePerformanceAndConsumptionChartsContext } from '../providers/PerformanceAndConsumptionChartsContext'
import { useUserSettings } from 'src/Hooks'

const useStyles = makeStyles()(theme => ({
  presetChartsWrapper: {
    height: '100%',
  },
  defaultCharts: {
    paddingTop: theme.spacing(2),

    [theme.breakpoints.down('md')]: {
      maxHeight: 'none',
    },
  },
  defaultCharts2: {
    paddingTop: theme.spacing(2),

    [theme.breakpoints.down('md')]: {
      maxHeight: 'none',
    },
  },
  chartGroupContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  roundedPaper: {
    borderRadius: theme.shape.borderRadius,
  },
}))

interface Props {
  performanceChart: React.ReactElement
  directConsumptionCharts: MappedChartType[]
}

enum PresetChartButtonValues {
  Building = 0,
  Consumption = 1,
}

/**
 * Component for performance and consumption charts
 */
const PerformanceAndConsumptionCharts: React.FC<Props> = ({ performanceChart, directConsumptionCharts }) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const { updateUserSettings } = useUserSettings()
  const { presetChartToDisplay, setPresetChartToDisplay } = usePerformanceAndConsumptionChartsContext()
  const doDisplayBuildingChart = presetChartToDisplay === PresetChartType.Building
  const activeButton = doDisplayBuildingChart ? PresetChartButtonValues.Building : PresetChartButtonValues.Consumption

  const handleButtonClick = async (newPresetChart: PresetChartTypes): Promise<void> => {
    setPresetChartToDisplay(newPresetChart)
    await updateUserSettings({ presetChart: newPresetChart })
  }

  const buildingChartsActions: TabButtonOption[] = [
    {
      id: 'systemchart-building-chart-button',
      onChange: () => handleButtonClick(PresetChartType.Building),
      label: t('systemCharts.buildingChartsButton'),
      dataQa: DASHBOARD_BUILDING_CHART_BUTTON_ID,
    },
    {
      id: 'systemchart-direct-consumption-chart-button',
      onChange: () => handleButtonClick(PresetChartType.Consumption),
      label: t('systemCharts.directConsumptionButton'),
      dataQa: DASHBOARD_DIRECT_CONSUMPTION_CHART_BUTTON_ID,
    },
  ]

  return (
    <Box className={classes.presetChartsWrapper}>
      <Stack spacing={2} direction="column" alignItems={'center'}>
        <Box className={classes.chartGroupContainer}>
          <Box className={classes.buttonGroup}>
            <TabButtonGroup options={buildingChartsActions} active={activeButton} width="50%" />
          </Box>
          {doDisplayBuildingChart ? (
            <Box className={cx(classes.defaultCharts, classes.roundedPaper)}>{performanceChart}</Box>
          ) : (
            <Box className={cx(classes.defaultCharts2, classes.roundedPaper)}>{directConsumptionCharts.map(chart => chart.node)}</Box>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

export default PerformanceAndConsumptionCharts
